/**
 * Activity Type Mapping
 *
 * Maps database ActivityType enum values to the UI ActivityType
 * used by @cortex/ui's ActivityFeed component, plus human-readable messages.
 *
 * Follows the same pattern as request-mappers.ts.
 */

import type { ActivityType as DbActivityType } from '@cortex/database';
import type { ActivityType as UiActivityType } from '@cortex/ui';

interface ActivityMapping {
  uiType: UiActivityType;
  message: (data?: Record<string, unknown>) => string;
}

const ACTIVITY_MAP: Record<DbActivityType, ActivityMapping> = {
  // CREATE stage
  request_created: {
    uiType: 'info',
    message: () => 'Request created',
  },
  request_updated: {
    uiType: 'config',
    message: () => 'Request updated',
  },

  // INTERVIEW stage
  interview_started: {
    uiType: 'info',
    message: () => 'Interview started',
  },
  interview_message: {
    uiType: 'message',
    message: () => 'Interview message',
  },
  interview_completed: {
    uiType: 'success',
    message: () => 'Interview completed',
  },

  // DEVELOP stage
  develop_started: {
    uiType: 'code',
    message: () => 'Development started',
  },
  develop_progress: {
    uiType: 'automated',
    message: () => 'Development in progress',
  },
  develop_completed: {
    uiType: 'success',
    message: () => 'Development completed',
  },
  develop_failed: {
    uiType: 'error',
    message: () => 'Development failed',
  },
  validation_passed: {
    uiType: 'success',
    message: () => 'Validation passed',
  },
  validation_failed: {
    uiType: 'error',
    message: () => 'Validation failed',
  },

  // REVIEW stage
  review_started: {
    uiType: 'info',
    message: () => 'Ready for review',
  },
  revision_requested: {
    uiType: 'warning',
    message: () => 'Revision requested',
  },
  changes_accepted: {
    uiType: 'success',
    message: () => 'Changes accepted',
  },
  preview_deployed: {
    uiType: 'deploy',
    message: (d) =>
      d?.url ? `Preview deployed: ${String(d.url)}` : 'Preview deployed',
  },

  // FINALIZE stage
  finalize_started: {
    uiType: 'info',
    message: () => 'Finalization started',
  },
  pr_created: {
    uiType: 'deploy',
    message: (d) =>
      d?.pr_number ? `PR #${String(d.pr_number)} created` : 'PR created',
  },
  work_item_synced: {
    uiType: 'config',
    message: () => 'Work item synced',
  },

  // TERMINAL
  request_closed: {
    uiType: 'success',
    message: () => 'Request completed',
  },

  // SUPPORTING
  comment_added: {
    uiType: 'message',
    message: () => 'Comment added',
  },
  assignment_changed: {
    uiType: 'config',
    message: () => 'Assignment changed',
  },
  status_changed: {
    uiType: 'config',
    message: () => 'Status changed',
  },
  notification_sent: {
    uiType: 'info',
    message: () => 'Notification sent',
  },
  error_occurred: {
    uiType: 'error',
    message: (d) =>
      d?.error_message ? `Error: ${String(d.error_message)}` : 'Error occurred',
  },
};

/**
 * Maps a database activity type to the UI ActivityType.
 */
export function mapDbActivityToUiType(dbType: DbActivityType): UiActivityType {
  return ACTIVITY_MAP[dbType]?.uiType ?? 'info';
}

/**
 * Gets a human-readable message for a database activity type.
 */
export function getActivityMessage(
  dbType: DbActivityType,
  data?: Record<string, unknown>
): string {
  return ACTIVITY_MAP[dbType]?.message(data) ?? dbType.replace(/_/g, ' ');
}
